import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactToPrint from "react-to-print";
import axios from "axios";
import { Container } from "@material-ui/core";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import InputBase from "@mui/material/InputBase";
import MuiButton from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Domain } from "Domain";
import PrintTable from "components/Table/Table3.js";
import LogoHeader from "assets/img/logo_transp.png";
import image from "assets/img/bg_img.jpg";
import {
  DownloadSimple,
  MagnifyingGlass,
  PencilSimple,
  Stethoscope,
} from "@phosphor-icons/react";

const modalityOptions = [
  { value: 1, label: "CT/X-Ray Procedures", apiType: "CT", accent: "#2f72f2" },
  { value: 2, label: "MRI Procedures", apiType: "MR", accent: "#16833a" },
];

export default function PriceListPage() {
  const [priceData, setPriceData] = useState([]);
  const [priceDataDraft, setPriceDataDraft] = useState([]);
  const [selectedValue, setSelectedValue] = useState(modalityOptions[0]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [print, setPrint] = useState([]);
  let componentRef = useRef();

  useEffect(() => {
    let formData = new FormData();
    formData.append("Status", "Active");
    formData.append("Type", selectedValue.apiType);
    axios({
      method: "post",
      url: Domain + "/GetAllPriceData.php",
      data: formData,
      config: { headers: { "Content-Type": "multipart/form-data" } },
    })
      .then((response) => {
        if (response.data.TotalCount != 0) {
          setPriceData(response.data.data);
          setPriceDataDraft([]);
        } else {
          setPriceData([]);
          setPriceDataDraft([]);
        }
      })
      .catch((response) => console.log(response));
  }, [selectedValue]);

  useEffect(() => {
    setPage(0);
  }, [selectedValue, search]);

  const activeRows = priceDataDraft.length > 0 ? priceDataDraft : priceData;
  const filteredRows = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return activeRows;
    return activeRows.filter((item) =>
      (item.Proceedure || "").toLowerCase().includes(value)
    );
  }, [activeRows, search]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  useEffect(() => {
    setPrint(
      activeRows.map((item) => ({
        Proceedure: item.Proceedure,
        Price: item.Price,
      }))
    );
  }, [activeRows]);

  function updateRow(index, field, value) {
    const source = priceDataDraft.length > 0 ? priceDataDraft : priceData;
    const nextData = source.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: value } : item
    );
    setPriceDataDraft(nextData);
  }

  const inputStyles = {
    color: "#1e293b",
    fontSize: "14px",
    fontWeight: 700,
    px: 1,
    py: 0.25,
    borderRadius: "8px",
    border: "1px solid transparent",
    "&:focus-within": {
      borderColor: "#d8e7ff",
      background: "#f8fbff",
    },
  };

  const headCellStyles = {
    fontSize: "12px",
    fontWeight: 800,
    color: "#64748b",
    letterSpacing: 0,
    border: 0,
    background: "#fbfdff",
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 28, fontWeight: 900, color: "#162033" }}>
            Price List
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#64748b", mt: 0.6 }}>
            Review, edit and export procedure pricing.
          </Typography>
        </Box>
        <ReactToPrint
          documentTitle={
            selectedValue.value === 1
              ? "THS CT STUDY PRICE LIST"
              : "THS MRI PRICE LIST"
          }
          trigger={() => (
            <MuiButton
              variant="contained"
              startIcon={<DownloadSimple size={18} weight="duotone" />}
              sx={{
                background: "#2f72f2",
                fontWeight: 900,
                boxShadow: "0 8px 18px rgba(47,114,242,0.22)",
                "&:hover": { background: "#1d5fce" },
              }}
            >
              Download PDF
            </MuiButton>
          )}
          content={() => componentRef}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
          gap: 2,
          mb: 2.5,
        }}
      >
        {modalityOptions.map((option) => {
          const active = selectedValue.value === option.value;
          return (
            <Paper
              key={option.value}
              elevation={0}
              onClick={() => setSelectedValue(option)}
              sx={{
                p: 2,
                borderRadius: "8px",
                border: active ? "1px solid #2f72f2" : "1px solid #e8eef7",
                background: active ? "#f4f8ff" : "#ffffff",
                cursor: "pointer",
                boxShadow: "0 14px 34px rgba(15,23,42,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "8px",
                    display: "grid",
                    placeItems: "center",
                    color: option.accent,
                    background: active ? "#ffffff" : "#f4f7fb",
                  }}
                >
                  <Stethoscope size={18} weight="duotone" />
                </Box>
                <Box>
                  <Typography
                    sx={{ fontSize: 15, fontWeight: 900, color: "#162033" }}
                  >
                    {option.label}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                    {option.apiType} tariff table
                  </Typography>
                </Box>
              </Box>
              {active && <Chip size="small" label="Active" color="primary" />}
            </Paper>
          );
        })}
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "8px",
          border: "1px solid #e8eef7",
          background: "#ffffff",
          boxShadow: "0 14px 34px rgba(15,23,42,0.06)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.25,
            borderBottom: "1px solid #e8eef7",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: 14, color: "#0f172a", fontWeight: 800 }}
            >
              {filteredRows.length} procedures available
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.2 }}>
              {selectedValue.label}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              background: "#f8fbff",
              border: "1px solid #e1eaf6",
              borderRadius: "8px",
              px: 1.5,
              py: 0.5,
              minWidth: { xs: "100%", sm: 260 },
            }}
          >
            <MagnifyingGlass size={18} weight="bold" color="#94a3b8" />
            <InputBase
              placeholder="Search procedures..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ fontSize: "14px", flex: 1 }}
            />
          </Box>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="70" sx={headCellStyles}>
                  No
                </TableCell>
                <TableCell sx={headCellStyles}>Procedure</TableCell>
                <TableCell width="150" sx={headCellStyles}>
                  Price
                </TableCell>
                <TableCell width="78" align="right" sx={headCellStyles}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{ border: 0, py: 3 }}
                  >
                    <Typography sx={{ color: "#94a3b8", fontSize: "14px" }}>
                      No procedures found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRows.map((item, visibleIndex) => {
                  const realIndex = activeRows.indexOf(item);
                  return (
                    <TableRow
                      key={`${item.Proceedure}-${realIndex}`}
                      hover
                      sx={{
                        "&:last-child td": { border: 0 },
                        transition: "background 0.15s",
                        "&:hover": { background: "#f8fbff" },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: "13px",
                          color: "#64748b",
                          borderBottom: "1px solid #eef3f9",
                          py: 0.9,
                        }}
                      >
                        {page * rowsPerPage + visibleIndex + 1}
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: "1px solid #eef3f9",
                          py: 0.55,
                          width: "auto",
                        }}
                      >
                        <InputBase
                          onChange={(event) =>
                            updateRow(
                              realIndex,
                              "Proceedure",
                              event.target.value
                            )
                          }
                          value={item.Proceedure}
                          id={`${realIndex}-procedure`}
                          fullWidth
                          sx={inputStyles}
                        />
                      </TableCell>
                      <TableCell
                        sx={{ borderBottom: "1px solid #eef3f9", py: 0.55 }}
                      >
                        <InputBase
                          onChange={(event) =>
                            updateRow(realIndex, "Price", event.target.value)
                          }
                          value={item.Price}
                          id={`${realIndex}-price`}
                          fullWidth
                          sx={{
                            ...inputStyles,
                            fontWeight: 800,
                          }}
                        />
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ borderBottom: "1px solid #eef3f9", py: 0.4 }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "8px",
                            background: "#e8f0fe",
                            color: "#2f72f2",
                            display: "inline-grid",
                            placeItems: "center",
                          }}
                        >
                          <PencilSimple size={17} weight="duotone" />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{ borderTop: "1px solid #e8eef7" }}
        />
      </Paper>

      <div style={{ display: "none" }}>
        <div
          ref={(el) => (componentRef = el)}
          style={{
            backgroundImage: "url(" + image + ")",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
          }}
        >
          <center>
            <img src={LogoHeader} alt="" width="250" />
            <h3>
              <b>Telemed Healthcare Solutions</b>
            </h3>
            <span style={{ fontSize: 24, marginBottom: 0, marginTop: 0 }}>
              <b> Day Coverage 8 AM - 11 PM</b>
            </span>
          </center>
          <Container style={{ padding: 50, paddingTop: 0, paddingBottom: 7 }}>
            <span style={{ fontSize: 25 }}>
              <PrintTable
                tableHeaderColor="info"
                tableHead={[selectedValue.label, "INR"]}
                tableData={print}
                loading={false}
                noAction={true}
              />
            </span>
            <p>
              <span style={{ fontSize: 19 }}>
                <b>Please Note:</b> For night hawk support between 11pm-8am, Rs.
                20/- for X-ray, Rs. 100/- for CT and Rs. 150/- for MRI,
                additional per study shall be charged. Billing will be on 30-day
                cycle, non-payment within 7 days will result in termination of
                services. Penalty and interest will be charges as per MSMEact.
                After successful contract of 1 year there will be a hike of 12%
                to the clients.
              </span>
            </p>
          </Container>
        </div>
      </div>
    </Box>
  );
}
